import { App } from "@capacitor/app";
import { Browser } from "@capacitor/browser";
import { Capacitor, CapacitorCookies, registerPlugin } from "@capacitor/core";
import { Haptics, ImpactStyle } from "@capacitor/haptics";
import { PrivacyScreen } from "@capacitor/privacy-screen";
import { SplashScreen } from "@capacitor/splash-screen";
import { StatusBar, Style } from "@capacitor/status-bar";
import { safeDestination } from "../../public/shared/socials/socialLinkWidget.js";
import {
  NATIVE_GUEST_FIXTURE,
  parseNativeAuthCallback,
  resolveApprovedEpicUrl,
  resolveMobileApiPrefix,
  resolveMobileSystemOrigin
} from "./environment";
import type {
  NativeAuthClient,
  NativeBridge,
  NativeGoogleSignInOptions,
  NativeGoogleSignInResult
} from "./types";

interface EpicGoogleCredentialPlugin {
  signIn(options: NativeGoogleSignInOptions): Promise<NativeGoogleSignInResult>;
  clearCredentialState(): Promise<void>;
}

const EpicGoogleCredential = registerPlugin<EpicGoogleCredentialPlugin>("EpicGoogleCredential");

export function shouldProtectScreen(sessionState: string, foreground: boolean, visible: boolean) {
  return sessionState !== "ready" || !foreground || !visible;
}

function nativePlatform(): "browser" | "ios" | "android" {
  const platform = Capacitor.getPlatform();
  return platform === "ios" || platform === "android" ? platform : "browser";
}

function nativeAuthClientForApp(platform: "browser" | "ios" | "android", appId: string): NativeAuthClient | null {
  if (platform === "ios" && appId === "pro.ctek123.epic") return "ios-release";
  if (platform === "android" && appId === "pro.ctek123.epic") return "android-release";
  if (platform === "android" && appId === "pro.ctek123.epic.dev") return "android-dev";
  return null;
}

function approvedSystemPath(path: string, origin: string) {
  const target = resolveApprovedEpicUrl(path, origin);
  if (!target) return null;
  const permitted = target.pathname === "/login/"
    || target.pathname === "/login/security.html"
    || target.pathname === "/api/instagram/login";
  return permitted ? target : null;
}

export function createNativeBridge(): NativeBridge {
  const platform = nativePlatform();
  const isNative = platform !== "browser";
  const systemOrigin = resolveMobileSystemOrigin(platform);
  let authClient: NativeAuthClient | null = null;
  let authClientPromise: Promise<NativeAuthClient | null> | null = null;
  let privacyUpdate: Promise<void> = Promise.resolve();
  async function resolveAuthClient() {
    if (!isNative) return null;
    if (!authClientPromise) {
      authClientPromise = App.getInfo()
        .then((info) => nativeAuthClientForApp(platform, info.id))
        .catch(() => null)
        .then((client) => {
          authClient = client;
          return client;
        });
    }
    return authClientPromise;
  }
  return {
    platform,
    get authClient() {
      return authClient;
    },
    apiPrefix: resolveMobileApiPrefix(platform),
    isNative,
    supportsHaptics: isNative,
    supportsNativeGoogleSignIn: platform === "android" && !NATIVE_GUEST_FIXTURE,
    supportsSecureStorage: false,
    supportsShare: typeof navigator !== "undefined" && typeof navigator.share === "function",
    async initialize() {
      if (!isNative) return;
      // The first local screen is already rendered. Network/auth readiness must
      // never keep Android's launch splash over loading or recovery controls.
      await SplashScreen.hide().catch(() => undefined);
      await resolveAuthClient();
      await Promise.allSettled([
        StatusBar.setOverlaysWebView({ overlay: false }),
        StatusBar.setStyle({ style: Style.Light }),
        ...(platform === "android" ? [StatusBar.setBackgroundColor({ color: "#0f1117" })] : [])
      ]);
    },
    async setSensitiveMode(enabled) {
      if (!isNative) return;
      // Serialize transitions so a late background call cannot lock the Inbox.
      privacyUpdate = privacyUpdate.then(async () => {
        try {
          if (enabled) {
            await PrivacyScreen.enable({
              android: { dimBackground: true, preventScreenshots: true, privacyModeOnActivityHidden: "splash" },
              ios: { blurEffect: "dark" }
            });
          } else {
            await PrivacyScreen.disable();
          }
        } catch (_) {
          // Backend authorization remains authoritative if native privacy support fails.
        }
      });
      await privacyUpdate;
    },
    async impact() {
      if (!isNative) return;
      try {
        await Haptics.impact({ style: ImpactStyle.Light });
      } catch (_) {
        // Haptics are nonessential feedback.
      }
    },
    async signInWithGoogle(options) {
      if (platform !== "android" || NATIVE_GUEST_FIXTURE) {
        throw Object.assign(new Error("Native Google sign-in is not supported on this platform."), {
          code: "GOOGLE_SIGN_IN_UNAVAILABLE"
        });
      }
      return EpicGoogleCredential.signIn(options);
    },
    async openSystemPath(path) {
      if (NATIVE_GUEST_FIXTURE) return false;
      const target = approvedSystemPath(path, systemOrigin);
      if (!target) return false;
      if (isNative) {
        if (target.pathname === "/login/") {
          const resolvedAuthClient = await resolveAuthClient();
          if (!resolvedAuthClient) return false;
          target.searchParams.set("nativePlatform", platform);
          target.searchParams.set("nativeClient", resolvedAuthClient);
          if (!target.searchParams.has("next")) target.searchParams.set("next", "/mobile/");
        }
        await Browser.open({ url: target.toString(), presentationStyle: "fullscreen" });
      } else if (typeof window !== "undefined") {
        window.location.assign(`${target.pathname}${target.search}`);
      } else return false;
      return true;
    },
    async clearSession() {
      if (!isNative) return;
      await Promise.allSettled([
        CapacitorCookies.deleteCookie({ url: systemOrigin, key: "epic.sid" }),
        ...(platform === "android" && !NATIVE_GUEST_FIXTURE ? [EpicGoogleCredential.clearCredentialState()] : [])
      ]);
    },
    async openProfileLink(value) {
      if (NATIVE_GUEST_FIXTURE) return false;
      const url = typeof value === "string" && value.length <= 2048 ? safeDestination(value) : null;
      if (!url) return false;
      if (isNative) await Browser.open({ url, presentationStyle: "fullscreen" });
      else if (typeof window !== "undefined") window.open(url, "_blank", "noopener,noreferrer");
      else return false;
      return true;
    },
    async onAppStateChange(listener) {
      if (!isNative) return async () => undefined;
      const handle = await App.addListener("appStateChange", ({ isActive }) => {
        if (isActive) void SplashScreen.hide().catch(() => undefined);
        listener(isActive);
      });
      return () => handle.remove();
    },
    async onAppUrlOpen(listener) {
      if (!isNative || NATIVE_GUEST_FIXTURE) return async () => undefined;
      const resolvedAuthClient = await resolveAuthClient();
      if (!resolvedAuthClient) return async () => undefined;
      const handleUrl = (url: string) => {
        const target = resolveApprovedEpicUrl(url);
        const authCallback = parseNativeAuthCallback(url, platform, resolvedAuthClient);
        if (authCallback || target?.pathname.startsWith("/mobile/")) {
          void Browser.close().catch(() => undefined);
          listener(authCallback ? url : target!.toString());
        }
      };
      const handle = await App.addListener("appUrlOpen", ({ url }) => handleUrl(url));
      const launch = await App.getLaunchUrl();
      if (launch?.url) handleUrl(launch.url);
      return () => handle.remove();
    }
  };
}
